import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@optools/components/ui/dropdown-menu';
import { cn } from '@optools/components/ui/utils';
import { appEnvironment } from '@optools/services/config/appConfig';
import { APP_ENVIRONMENTS } from '@optools/services/config/types';
import { Link, LinkProps, useMatches } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';

import { Button } from '../ui/button';

const isLowerEnvironment =
  appEnvironment === APP_ENVIRONMENTS.QA || appEnvironment === APP_ENVIRONMENTS.NPD;

export interface SubNavigationProps {
  links?: SubNavigationItem[];
  title: string;
}

export function SubNavigation({ links, title }: SubNavigationProps) {
  return (
    <header
      className={cn('grid min-h-13.25 w-full items-center border-b lg:flex lg:gap-1', {
        'bg-[#813BF6] text-white': appEnvironment === APP_ENVIRONMENTS.QA,
        'bg-[#61082B] text-white': appEnvironment === APP_ENVIRONMENTS.NPD,
      })}
      id="sub-navigation">
      <div className="flex min-h-13 items-center pl-4 lg:min-h-0">
        <h1 className="text-lg font-semibold whitespace-nowrap lg:p-0">{title}</h1>
      </div>

      {links && (
        <nav className="flex w-full flex-1 items-center gap-1 overflow-x-auto border-t px-4 py-2 lg:border-t-0">
          {links.map((link) =>
            isSubNavigationGroup(link) ? (
              <SubNavigationDropdown key={link.title} {...link} />
            ) : (
              <SubNavigationLink key={link.label} {...link} />
            ),
          )}
        </nav>
      )}
    </header>
  );
}

export interface SubNavigationLinkProps {
  label: string;
  to: Exclude<LinkProps['to'], '/' | '.' | '..'>;
}

function SubNavigationLink({ label, to }: SubNavigationLinkProps) {
  return (
    <Button asChild variant="ghost">
      <Link
        key={label}
        className={cn(
          'text-muted-foreground [&.active]:text-foreground',
          isLowerEnvironment ? 'text-white/70 hover:text-foreground! [&.active]:text-white' : '',
        )}
        from="/"
        to={to}>
        {label}
      </Link>
    </Button>
  );
}

export interface SubNavigationGroupProps {
  links: SubNavigationLinkProps[];
  title: string;
}

export type SubNavigationItem = SubNavigationLinkProps | SubNavigationGroupProps;

function isSubNavigationGroup(item: SubNavigationItem): item is SubNavigationGroupProps {
  return 'links' in item;
}

function SubNavigationDropdown({ links, title }: SubNavigationGroupProps) {
  const matches = useMatches();
  const isMatch = matches.some((match) => match.pathname.includes(title.toLocaleLowerCase()));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            'text-muted-foreground',
            isMatch && 'text-foreground',
            isLowerEnvironment ? 'text-white/70 [&.active]:text-white' : '',
          )}
          variant="ghost">
          {title}
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-max" sideOffset={6}>
        {links.map(({ label, to }) => (
          <DropdownMenuItem key={label} asChild>
            <Link
              className={cn('text-muted-foreground [&.active]:text-foreground')}
              from="/"
              to={to}>
              {label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
